import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
} from '@angular/forms';
import {
  Injector,
  Input,
  Component,
  ViewChildren,
  QueryList,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { getFirstValidationError } from './inputs-utils';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  template: '',
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class ControlValueAccessorConnector
  implements ControlValueAccessor, OnInit, OnChanges
{
  @ViewChildren(FormControlDirective) set formControlDirectives(
    value: QueryList<FormControlDirective>
  ) {
    this.input = value.toArray()[0];
    this.valueAccessor = value.toArray()[0]?.valueAccessor ?? undefined;
    if (this.valueAccessor) {
      if (this.pendingOnChangeFn) {
        this.registerOnChange(this.pendingOnChangeFn);
        this.pendingOnChangeFn = undefined;
      }
      if (this.pendingOnTouchFn) {
        this.registerOnTouched(this.pendingOnTouchFn);
        this.pendingOnTouchFn = undefined;
      }
    }
  }

  @Input()
  formControl?: FormControl;

  @Input()
  formControlName?: string;

  @Input() set value(value: any) {
    this.control.setValue(value);
    this.writeValue(value);
  }

  @Input()
  validationTrigger = false;

  @Input()
  validateOnTouched = true;

  @Input()
  listenErrorsFromControl?: AbstractControl;

  pendingOnChangeFn?: (newValue: any) => any;
  pendingOnTouchFn?: (newValue: any) => any;

  valueAccessor?: ControlValueAccessor;
  input?: FormControlDirective;

  //eslint-disable-next-line
  @Input('error') set errorSetter(error: string | undefined) {
    this.error = error ?? '';
  }

  error = '';
  error$ = new BehaviorSubject(this.error);
  touched = false;

  onChange = (newValue: any) => {};

  onTouched = () => {};

  private _onChange(newValue: any) {}

  private _onTouched() {
    this.touched = true;
    this.checkErrors();
  }

  constructor(
    private injector: Injector,
    private translateServiceToInject: TranslateService
  ) {}

  ngOnInit(): void {
    this.control.valueChanges.subscribe(() => {
      this.checkErrors();
    });
    this.control.statusChanges.subscribe(() => {
      this.checkErrors();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['validationTrigger']) {
      this.checkErrors();
    }
  }

  get controlContainer() {
    return this.injector.get(ControlContainer);
  }

  get control(): FormControl {
    if (this.formControl) {
      return this.formControl;
    }
    if (this.formControlName) {
      return this.controlContainer?.control?.get(
        this.formControlName
      ) as FormControl;
    }
    this.formControl = new FormControl('');
    return this.formControl;
  }

  registerOnTouched(fn: any): void {
    if (!this.pendingOnTouchFn) {
      this.pendingOnTouchFn = fn;
    }
    const newOnTouchedFunction = () => {
      this._onTouched();
      fn();
    };
    this.onTouched = newOnTouchedFunction;
    if (this.valueAccessor) {
      this.valueAccessor?.registerOnTouched(newOnTouchedFunction);
    }
  }

  registerOnChange(fn: any): void {
    if (!this.pendingOnChangeFn) {
      this.pendingOnChangeFn = fn;
    }
    const newOnChangeFunction = (newValue: any) => {
      this._onChange(newValue);
      fn(newValue);
    };

    this.onChange = newOnChangeFunction;
    if (this.valueAccessor) {
      this.valueAccessor?.registerOnChange(newOnChangeFunction);
    }
  }

  writeValue(value: any): void {
    this.valueAccessor?.writeValue(value);
    this.checkErrors();
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.valueAccessor?.setDisabledState) {
      this.valueAccessor.setDisabledState(isDisabled);
    }
  }

  checkErrors() {
    if (this.validationTrigger || (this.touched && this.validateOnTouched)) {
      this.error = this.listenErrorsFromControl
        ? getFirstValidationError(
            this.listenErrorsFromControl,
            this.translateServiceToInject
          )
        : getFirstValidationError(this.control, this.translateServiceToInject);
      this.error$.next(this.error);
    }
  }
}
